import {
  Bot,
  Database,
  BarChart2,
  Mail,
  MessageSquare,
  CheckSquare,
  Cloud,
  Server,
  ShieldCheck,
  Zap,
  Headphones,
} from "lucide-react";
import type { ComponentType } from "react";
import type { LucideProps } from "lucide-react";

type Integration = {
  name: string;
  category: string;
  description: string;
  detail: string;
  icon: ComponentType<LucideProps>;
  iconBg: string;
  iconColor: string;
  status: "active" | "per-user";
};

const INTEGRATIONS: Integration[] = [
  {
    name: "Claude (Anthropic)",
    category: "AI",
    description: "Core AI assistant powering chat, report generation, dashboard tile creation, and onboarding.",
    detail: "Used across chat conversations, SQL generation, narrative reports, and the Brian AI dashboard.",
    icon: Bot,
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    status: "active",
  },
  {
    name: "Databricks SQL",
    category: "Data Warehouse",
    description: "Primary data warehouse for executing read-only SQL queries against your organisation's data.",
    detail: "Supports multiple datasources via OAuth M2M credentials. Powers all reports, alerts, and chat-driven analysis.",
    icon: Database,
    iconBg: "bg-red-50",
    iconColor: "text-red-600",
    status: "active",
  },
  {
    name: "Power BI",
    category: "Business Intelligence",
    description: "Microsoft Power BI integration for embedding reports and creating datasets from SQL queries.",
    detail: "Users connect their own Power BI account via Azure AD OAuth. Enables in-chat report embedding and dataset creation.",
    icon: BarChart2,
    iconBg: "bg-yellow-50",
    iconColor: "text-yellow-600",
    status: "per-user",
  },
  {
    name: "SendGrid",
    category: "Email",
    description: "Transactional email delivery for scheduled reports and password reset emails.",
    detail: "Sends daily report emails with retry logic (3 attempts). Also handles forgot-password flows.",
    icon: Mail,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    status: "active",
  },
  {
    name: "Slack",
    category: "Communication",
    description: "Reads configured Slack channels so the AI can surface trending topics and recent discussions.",
    detail: "Requires channels:history and channels:read scopes. Channel IDs are configured server-side.",
    icon: MessageSquare,
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    status: "active",
  },
  {
    name: "ClickUp",
    category: "Project Management",
    description: "Allows the AI to search and look up tasks and tickets from your ClickUp workspace.",
    detail: "Uses a personal API token. The AI can search tasks by keyword, status, assignee, priority, and due date.",
    icon: CheckSquare,
    iconBg: "bg-pink-50",
    iconColor: "text-pink-600",
    status: "active",
  },
  {
    name: "Zendesk",
    category: "Customer Support",
    description: "Connects your Zendesk account to surface ticket data and map brands to datasources.",
    detail: "Brands are fetched via the Zendesk REST API and mapped to internal datasources. Requires ZENDESK_SUBDOMAIN, ZENDESK_EMAIL, and ZENDESK_API_TOKEN environment variables.",
    icon: Headphones,
    iconBg: "bg-lime-50",
    iconColor: "text-lime-600",
    status: "active",
  },
  {
    name: "AWS S3",
    category: "Storage",
    description: "Cloud object storage for feedback attachments and screenshots submitted by users.",
    detail: "Bucket: brian-images-341754634529-eu-west-1-an (eu-west-1). Supports images, PDFs, CSVs up to 10 MB.",
    icon: Cloud,
    iconBg: "bg-orange-50",
    iconColor: "text-orange-600",
    status: "active",
  },
  {
    name: "PostgreSQL",
    category: "Database",
    description: "Primary application database storing users, conversations, reports, dashboards, and audit logs.",
    detail: "Managed via SQLAlchemy ORM and Alembic migrations. Runs on PostgreSQL 15.",
    icon: Server,
    iconBg: "bg-sky-50",
    iconColor: "text-sky-600",
    status: "active",
  },
  {
    name: "Two-Factor Auth (TOTP)",
    category: "Security",
    description: "Time-based one-time password 2FA for user accounts, compatible with any TOTP authenticator app.",
    detail: "Built with PyOTP. Users scan a QR code during setup. Enforced at login when enabled.",
    icon: ShieldCheck,
    iconBg: "bg-teal-50",
    iconColor: "text-teal-600",
    status: "active",
  },
  {
    name: "APScheduler",
    category: "Background Jobs",
    description: "In-process job scheduler for daily report emails, alert evaluation, and dashboard snapshots.",
    detail: "Jobs: daily reports (configurable cron), alert checks every 15 min, audit log purge daily at 3 AM.",
    icon: Zap,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    status: "active",
  },
];

const STATUS_STYLES = {
  active: "bg-green-50 text-green-700 border border-green-200",
  "per-user": "bg-blue-50 text-blue-700 border border-blue-200",
};

const STATUS_LABELS = {
  active: "Active",
  "per-user": "Per-user OAuth",
};

const CATEGORIES = [...new Set(INTEGRATIONS.map((i) => i.category))];

export default function Integrations() {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-base font-semibold text-gray-900">Integrations</h2>
        <p className="mt-1 text-sm text-gray-500">
          All external services and connections currently configured in this application.
        </p>
      </div>

      <div className="space-y-8">
        {CATEGORIES.map((category) => {
          const items = INTEGRATIONS.filter((i) => i.category === category);
          return (
            <div key={category}>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {category}
              </h3>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                {items.map((integration) => {
                  const Icon = integration.icon;
                  return (
                    <div key={integration.name} className="flex items-start gap-4 px-5 py-4">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${integration.iconBg}`}>
                        <Icon size={20} className={integration.iconColor} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">{integration.name}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[integration.status]}`}>
                            {STATUS_LABELS[integration.status]}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-gray-600">{integration.description}</p>
                        <p className="mt-1 text-xs text-gray-400">{integration.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
